//* Import Libraries
import React, { useState, useCallback, useEffect } from "react";

//* Import custom components
import { List } from "../../../component/list/list";
import { Loading } from "../../../component/common/Loading";

//* Import navigation hook
import { useNavigate } from "react-router-dom";

//* Import custom hooks
import { useOrganizations } from "../../../hooks/organization/useOrganization";

//* Import API services
import { AuthAPI } from "../../../services/apis/AuthAPI";

//* Import user roles
import { UserRole } from "../../../constants/enum/UserRole";

//* Import debounce function from lodash
var debounce = require("lodash.debounce");

interface User {
  role: UserRole;
}

//* Type for basic organization information
type Organizations = {
  id: string;
  name: string;
  responsibleId: string;
  logoPath: string;
  description: string;
};

export function OrganizationView() {
  // State variables
  const [me, setMe] = useState<User | null>(null);
  const token = localStorage.getItem("token") || "";

  // Destructure functions and variables from useOrganizations hook
  const {
    organizations,
    loading,
    setSearch,
    pagination,
    setPagination,
    deleteOrganization,
    refetch,
    changePage,
  } = useOrganizations(token);

  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [organizationToDelete, setOrganizationToDelete] = useState<
    string | null
  >(null);
  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const [isSearching, setIsSearching] = useState<boolean>(false);

  const navigate = useNavigate();

  // Debounced search function to handle input with delay
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      if (value.trim().length >= 3) {
        setSearch(value);
      } else {
        setSearch("");
        setPagination((prev: any) => ({ ...prev, currentPage: 1 }));
        refetch();
      }
      setIsSearching(false);
    }, 500),
    [setSearch, setPagination, refetch]
  );

  // Handle search input change
  const handleSearchInputChange = useCallback(
    (value: string) => {
      setSearchInputValue(value);
      setIsSearching(true);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  // Fetch user details on component mount
  useEffect(() => {
    async function fetchMe() {
      try {
        const response = await AuthAPI.me(token);
        setMe(response.data);
      } catch (error) {
        console.error("Error fetching user details:", error);
      }
    }
    fetchMe();
  }, [token]);

  // Confirm deletion of an organization
  const handleDeleteConfirm = useCallback(async () => {
    if (organizationToDelete !== null) {
      try {
        await deleteOrganization(organizationToDelete);
        setShowWarning(false);
        setOrganizationToDelete(null);
        refetch();
      } catch (error) {
        console.error("Error deleting organization:", error);
      }
    }
  }, [organizationToDelete, deleteOrganization, refetch]);

  // Handle click on delete action
  const handleDeleteActionClick = useCallback((id: string) => {
    setOrganizationToDelete(id);
    setShowWarning(true);
  }, []);

  // Handle pagination change
  const handleChangePagination = useCallback(
    (value: number) => {
      changePage(value);
    },
    [changePage]
  );

  if (!me) {
    return <Loading />;
  }

  return (
    <div className="row">
      <List
        hideManager
        search_input_value={searchInputValue}
        title={"Organizações"}
        not_found_message={"Nenhuma organização encontrada"}
        button_text="Adicionar organização"
        button_link="/organizations/create"
        search_text="Pesquise pelo nome da organização"
        onChangeSearchInput={handleSearchInputChange}
        deleting_message={
          "Ao deletar esta organização, ela não estará mais disponível no sistema"
        }
        onSearchButtonClick={refetch}
        onDeleteCancel={() => setShowWarning(false)}
        onDeleteConfirm={handleDeleteConfirm}
        onDeleteActionClick={handleDeleteActionClick}
        onEditActionClick={(id) => navigate(`/organizations/edit/${id}`)}
        onChangePagination={handleChangePagination}
        show_warning={showWarning}
        loading_screen={loading || isSearching}
        items={organizations.map((organization: Organizations) => ({
          ...organization,
          key: organization.id,
        }))}
        pagination={{
          last_page: pagination.totalPages,
          current_page: pagination.currentPage,
        }}
        fields={[
          { name: "name", type: "text", header: "Nome" },
          { name: "description", type: "text", header: "Descrição" },
        ]}
        loading_search={isSearching}
      />
    </div>
  );
}
